import React from 'react';
import {
  TextField,
  Grid,
  Checkbox,
  FormControlLabel,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Autocomplete,
  FormHelperText,
} from '@mui/material';
import {
  surgeryData,
  surgeonList,
  assistantSurgeonList,
  anesthesiologistList,
  nursingStaffList,
  theatreOptions,
  allergiesOptions,
  insuranceOptions,
  operationStatusOptions,
} from '../../../utils/dataConstants';

/**
 * Shared OT Booking form fields used by both Add and Edit pages.
 * All state and handlers come from the parent via props.
 */
const OTBookingForm = ({
  formData,
  errors,
  handleChange,
  handleAllergyChange,
  handlePatientChange,
  patients,            // list of patients (for Add: filtered; for Edit: all)
  readOnly = false,    // true when fields like patient name shouldn't be editable
  isEdit = false,
}) => {
  const inputStyle = {
    "& .MuiInputBase-input": {
      color: "inherit",
    },
    "& input::-webkit-calendar-picker-indicator": {
      filter: "invert(0.5)", // Make sure indicator is visible
    }
  };

  return (
    <Grid container spacing={2}>
      {/* IP No */}
      <Grid item xs={12} sm={6}>
        <TextField
          label="IP No"
          name="ip_no"
          type="number"
          value={formData.ip_no}
          onChange={handleChange}
          fullWidth
          error={!!errors.ip_no}
          helperText={errors.ip_no}
        />
      </Grid>

      {/* Patient Name */}
      <Grid item xs={12} sm={6}>
        {isEdit ? (
          <TextField
            label="Patient Name"
            value={formData.patient_name}
            fullWidth
            InputProps={{ readOnly: true }}
          />
        ) : (
          <Autocomplete
            options={patients || []}
            getOptionLabel={(option) => option.name || ""}
            renderOption={(props, option) => (
              <li {...props}>{option.name} – UHID: {option.uhid_no}</li>
            )}
            onChange={(_, newValue) => handlePatientChange(newValue)}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Patient Name"
                fullWidth
                error={!!errors.uhid_no}
                helperText={errors.uhid_no}
              />
            )}
          />
        )}
      </Grid>

      {/* Booking Datetime */}
      <Grid item xs={12} sm={6}>
        <TextField
          label="Booking Date & Time"
          name="booking_datetime"
          type="datetime-local"
          value={formData.booking_datetime}
          onChange={handleChange}
          fullWidth
          InputLabelProps={{ shrink: true }}
          sx={inputStyle}
        />
      </Grid>

      {/* UHID No (read-only) */}
      <Grid item xs={12} sm={6}>
        <TextField
          label="UHID No"
          name="uhid_no"
          value={formData.uhid_no}
          fullWidth
          InputProps={{ readOnly: true }}
        />
      </Grid>

      {/* Allergies */}
      <Grid item xs={12}>
        <Autocomplete
          multiple
          freeSolo
          options={allergiesOptions}
          value={formData.allergies_medical_alerts || []}
          onChange={(_, newValue) => handleAllergyChange(newValue)}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Allergies / Medical Alerts"
              placeholder="Select or type"
              fullWidth
            />
          )}
        />
      </Grid>

      {/* Surgery Type */}
      <Grid item xs={12}>
        <FormControl fullWidth required error={!!errors.type_of_surgery}>
          <InputLabel>Type of Surgery / Procedure</InputLabel>
          <Select
            name="type_of_surgery"
            value={formData.type_of_surgery}
            label="Type of Surgery / Procedure"
            onChange={handleChange}
          >
            {surgeryData.map((s, i) => (
              <MenuItem key={i} value={s.diagnosisDescription}>{s.diagnosisDescription}</MenuItem>
            ))}
          </Select>
          <FormHelperText>{errors.type_of_surgery || 'Select surgery to auto-fill codes below'}</FormHelperText>
        </FormControl>
      </Grid>

      {/* Auto-filled codes */}
      <Grid item xs={12} sm={6}>
        <TextField label="Surgical Code (ICD-10)" name="surgical_code" value={formData.surgical_code} fullWidth InputProps={{ readOnly: true }} helperText="Auto-filled" />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField label="Procedure Code (CPT)" name="procedure_code" value={formData.procedure_code} fullWidth InputProps={{ readOnly: true }} helperText="Auto-filled" />
      </Grid>
      <Grid item xs={12}>
        <TextField label="Procedure Description" name="type_of_procedure" value={formData.type_of_procedure} fullWidth InputProps={{ readOnly: true }} helperText="Auto-filled" />
      </Grid>

      {/* Surgery Datetime */}
      <Grid item xs={12} sm={6}>
        <TextField
          label="Surgery Date & Time"
          name="surgery_datetime"
          type="datetime-local"
          value={formData.surgery_datetime}
          onChange={handleChange}
          fullWidth
          required
          InputLabelProps={{ shrink: true }}
          sx={inputStyle}
          inputProps={{ min: new Date().toISOString().slice(0, 16) }}
          error={!!errors.surgery_datetime}
          helperText={errors.surgery_datetime}
        />
      </Grid>

      {/* Expected Duration */}
      <Grid item xs={12} sm={6}>
        <TextField
          label="Expected Duration (hours)"
          name="expected_duration"
          type="number"
          inputProps={{ min: 0.5, step: 0.5 }}
          value={formData.expected_duration}
          onChange={handleChange}
          fullWidth
          required
        />
      </Grid>

      {/* Diagnosis */}
      <Grid item xs={12}>
        <TextField
          label="Diagnosis / Reason for Surgery"
          name="diagnosis_reason"
          value={formData.diagnosis_reason}
          onChange={handleChange}
          fullWidth
          required
        />
      </Grid>

      {/* Operating Theatre */}
      <Grid item xs={12} sm={6}>
        <FormControl fullWidth required error={!!errors.preferred_operating_theatre}>
          <InputLabel>Preferred Operating Theatre</InputLabel>
          <Select
            name="preferred_operating_theatre"
            value={formData.preferred_operating_theatre}
            label="Preferred Operating Theatre"
            onChange={handleChange}
          >
            {theatreOptions.map((o, i) => <MenuItem key={i} value={o}>{o}</MenuItem>)}
          </Select>
          <FormHelperText>{errors.preferred_operating_theatre}</FormHelperText>
        </FormControl>
      </Grid>

      {/* Priority */}
      <Grid item xs={12} sm={isEdit ? 6 : 6}>
        <FormControl fullWidth required error={!!errors.priority_status}>
          <InputLabel>Priority Status</InputLabel>
          <Select
            name="priority_status"
            value={formData.priority_status}
            label="Priority Status"
            onChange={handleChange}
          >
            <MenuItem value="Emergency">Emergency</MenuItem>
            <MenuItem value="Urgent">Urgent</MenuItem>
            <MenuItem value="Elective">Elective</MenuItem>
          </Select>
          <FormHelperText>{errors.priority_status}</FormHelperText>
        </FormControl>
      </Grid>


      {/* Primary Surgeon */}
      <Grid item xs={12} sm={6}>
        <TextField select label="Primary Surgeon" name="primary_surgeon_name" value={formData.primary_surgeon_name} onChange={handleChange} fullWidth required error={!!errors.primary_surgeon_name} helperText={errors.primary_surgeon_name}>
          {surgeonList.map(s => <MenuItem key={s.id} value={s.name}>{s.name}</MenuItem>)}
        </TextField>
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField label="Surgeon ID" name="primary_surgeon_id" value={formData.primary_surgeon_id} fullWidth InputProps={{ readOnly: true }} />
      </Grid>

      {/* Assistant Surgeon */}
      <Grid item xs={12} sm={6}>
        <TextField select label="Assistant Surgeon" name="assistant_surgeon_names" value={formData.assistant_surgeon_names} onChange={handleChange} fullWidth>
          {assistantSurgeonList.map(a => <MenuItem key={a.id} value={a.name}>{a.name}</MenuItem>)}
        </TextField>
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField label="Assistant Surgeon ID" name="assistant_surgeon_ids" value={formData.assistant_surgeon_ids} fullWidth InputProps={{ readOnly: true }} />
      </Grid>

      {/* Anesthesiologist */}
      <Grid item xs={12} sm={6}>
        <TextField select label="Anesthesiologist" name="anesthesiologist_name" value={formData.anesthesiologist_name} onChange={handleChange} fullWidth required>
          {anesthesiologistList.map(a => <MenuItem key={a.id} value={a.name}>{a.name}</MenuItem>)}
        </TextField>
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField label="Anesthesiologist ID" name="anesthesiologist_id" value={formData.anesthesiologist_id} fullWidth InputProps={{ readOnly: true }} />
      </Grid>

      {/* Nursing Staff */}
      <Grid item xs={12} sm={6}>
        <TextField select label="Nursing Staff" name="nursing_staff_names" value={formData.nursing_staff_names} onChange={handleChange} fullWidth required>
          {nursingStaffList.map(n => <MenuItem key={n.id} value={n.name}>{n.name}</MenuItem>)}
        </TextField>
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField label="Nursing Staff ID" name="nursing_staff_ids" value={formData.nursing_staff_ids} fullWidth InputProps={{ readOnly: true }} />
      </Grid>

      {/* Checkboxes */}
      <Grid item xs={12} sm={6}>
        <FormControlLabel control={<Checkbox checked={!!formData.preoperative_tests_required} onChange={handleChange} name="preoperative_tests_required" />} label="Preop Tests Required" />
      </Grid>
      <Grid item xs={12} sm={6}>
        <FormControlLabel control={<Checkbox checked={!!formData.special_equipment_needed} onChange={handleChange} name="special_equipment_needed" />} label="Special Equipment Needed" />
      </Grid>
      <Grid item xs={12} sm={6}>
        <FormControlLabel control={<Checkbox checked={!!formData.blood_products_required} onChange={handleChange} name="blood_products_required" />} label="Blood Products Required" />
      </Grid>
      <Grid item xs={12} sm={6}>
        <FormControlLabel control={<Checkbox checked={!!formData.fasting_instructions_given} onChange={handleChange} name="fasting_instructions_given" />} label="Fasting Instructions Given" />
      </Grid>
      <Grid item xs={12} sm={6}>
        <FormControlLabel control={<Checkbox checked={!!formData.preoperative_medications_provided} onChange={handleChange} name="preoperative_medications_provided" />} label="Preop Medications Provided" />
      </Grid>
      <Grid item xs={12} sm={6}>
        <FormControlLabel control={<Checkbox checked={!!formData.consent_forms_completed} onChange={handleChange} name="consent_forms_completed" />} label="Consent Forms Completed" />
      </Grid>
      <Grid item xs={12} sm={6}>
        <FormControlLabel control={<Checkbox checked={!!formData.is_major_ot} onChange={handleChange} name="is_major_ot" />} label="Major OT" />
      </Grid>

      {/* Payment */}
      <Grid item xs={12} sm={formData.payment_method === 'Insurance' ? 6 : 12}>
        <FormControl fullWidth required>
          <InputLabel>Payment Method</InputLabel>
          <Select 
            name="payment_method" 
            value={formData.payment_method} 
            label="Payment Method" 
            onChange={handleChange}
          >
            <MenuItem value="Cash">Cash</MenuItem>
            <MenuItem value="Insurance">Insurance</MenuItem>
            <MenuItem value="Online">Online</MenuItem>
          </Select>
        </FormControl>
      </Grid>

      {/* Insurance (Only show if payment method is Insurance) */}
      {formData.payment_method === 'Insurance' && (
        <Grid item xs={12} sm={6}>
          <Autocomplete
            freeSolo
            options={insuranceOptions}
            value={formData.insurance_details || ''}
            onInputChange={(_, newVal) => handleChange({ target: { name: 'insurance_details', value: newVal || '' } })}
            renderInput={(params) => (
              <TextField {...params} label="Insurance Provider Details" fullWidth required />
            )}
          />
        </Grid>
      )}

      {/* Room No */}
      <Grid item xs={12} sm={6}>
        <TextField
          label="Room No"
          name="room_no"
          value={formData.room_no}
          onChange={handleChange}
          fullWidth
          required
          error={!!errors.room_no}
          helperText={errors.room_no}
        />
      </Grid>

      {/* Fields only for Edit Mode: Operation Status, Actual Start/End Times */}
      {isEdit && (
        <>
          <Grid item xs={12} sm={4}>
            <TextField
              select
              label="Operation Status"
              name="operation_status"
              value={formData.operation_status || ""}
              onChange={handleChange}
              fullWidth
              required
              error={!!errors.operation_status}
              helperText={errors.operation_status}
            >
              {operationStatusOptions.map((o, i) => (
                <MenuItem key={i} value={o}>{o}</MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12} sm={4}>
            <TextField
              label="Actual Start Time"
              name="actual_start_time"
              type="datetime-local"
              value={formData.actual_start_time || ""}
              onChange={handleChange}
              fullWidth
              InputLabelProps={{ shrink: true }}
              sx={inputStyle}
              error={!!errors.actual_start_time}
              helperText={errors.actual_start_time}
            />
          </Grid>

          <Grid item xs={12} sm={4}>
            <TextField
              label="Actual End Time"
              name="actual_end_time"
              type="datetime-local"
              value={formData.actual_end_time || ""}
              onChange={handleChange}
              fullWidth
              InputLabelProps={{ shrink: true }}
              sx={inputStyle}
              error={!!errors.actual_end_time}
              helperText={errors.actual_end_time}
            />
          </Grid>
        </>
      )}
    </Grid>
  );
};

export default OTBookingForm;
